import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextField,
  Chip,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Save, Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../server";
import Loading from "../global/Loading";

const UpdateSubject = ({ isOpen, subject, onClose, onSave, darkMode }) => {
  const [editSubject, setEditSubject] = useState(subject);
  const [newExam, setNewExam] = useState("");
  const [newChapter, setNewChapter] = useState({ name: "", topics: [] });
  const [newTopic, setNewTopic] = useState("");
  const [loading, setLoading] = useState(false);

  // Update local state when subject prop changes
  useEffect(() => {
    if (subject) {
      setEditSubject({ ...subject });
    }
  }, [subject]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const { data } = await axios.put(
        `${server}/subject/updateById`,
        {
          subject: editSubject?.subject,
          description: editSubject?.description,
          exams: editSubject?.exams,
          icon: editSubject?.icon,
          isPopular: editSubject?.isPopular,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            subjectId: editSubject?.id,
          },
        }
      );
      toast.success(data?.message);
      onSave(editSubject);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExam = () => {
    if (newExam.trim() && editSubject) {
      setEditSubject({
        ...editSubject,
        exams: [...editSubject.exams, newExam.trim()],
      });
      setNewExam("");
    }
  };

  const handleRemoveExam = (examToRemove) => {
    if (editSubject) {
      setEditSubject({
        ...editSubject,
        exams: editSubject.exams.filter((exam) => exam !== examToRemove),
      });
    }
  };

  const handleAddChapter = () => {
    if (newChapter.name.trim() && editSubject) {
      const chapterToAdd = {
        id: Date.now(), // Temporary ID
        name: newChapter.name.trim(),
        topics: [...newChapter.topics],
      };

      setEditSubject({
        ...editSubject,
        chapters: [...editSubject.chapters, chapterToAdd],
      });
      setNewChapter({ name: "", topics: [] });
    }
  };

  const handleRemoveChapter = (chapterIndex) => {
    if (editSubject) {
      const updatedChapters = editSubject.chapters.filter(
        (_, index) => index !== chapterIndex
      );
      setEditSubject({
        ...editSubject,
        chapters: updatedChapters,
      });
    }
  };

  const handleAddTopicToChapter = (chapterIndex) => {
    if (newTopic.trim() && editSubject) {
      const updatedChapters = [...editSubject.chapters];
      updatedChapters[chapterIndex].topics = [
        ...updatedChapters[chapterIndex].topics,
        newTopic.trim(),
      ];

      setEditSubject({
        ...editSubject,
        chapters: updatedChapters,
      });
      setNewTopic("");
    }
  };

  const handleRemoveTopicFromChapter = (chapterIndex, topicToRemove) => {
    if (editSubject) {
      const updatedChapters = [...editSubject.chapters];
      updatedChapters[chapterIndex].topics = updatedChapters[
        chapterIndex
      ].topics.filter((topic) => topic !== topicToRemove);

      setEditSubject({
        ...editSubject,
        chapters: updatedChapters,
      });
    }
  };

  const handleKeyPress = (e, type, chapterIndex = null) => {
    if (e.key === "Enter") {
      if (type === "exam") {
        handleAddExam();
      } else if (type === "chapter") {
        handleAddChapter();
      } else if (type === "topic" && chapterIndex !== null) {
        handleAddTopicToChapter(chapterIndex);
      }
    }
  };

  if (!editSubject) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2 },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalBg = darkMode ? "bg-gray-800" : "bg-white";
  const textPrimary = darkMode ? "text-white" : "text-gray-800";
  const textSecondary = darkMode ? "text-gray-300" : "text-gray-600";
  const headerBorder = darkMode ? "border-gray-700" : "border-gray-100";
  const inputBg = darkMode ? "bg-gray-700" : "bg-white";

  return (
    <AnimatePresence>
      {isOpen && editSubject && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`${modalBg} rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className={`flex items-center justify-between p-6 border-b ${headerBorder}`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-4xl">{editSubject.icon}</span>
                <h2 className={`text-2xl font-bold ${textPrimary}`}>
                  Edit {editSubject.subject}
                </h2>
              </div>
              <button
                onClick={onClose}
                className={`p-2 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                } rounded-full transition-colors duration-300`}
              >
                <Close className={textPrimary} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Subject Name */}
              <TextField
                fullWidth
                label="Subject Name"
                value={editSubject.subject}
                onChange={(e) =>
                  setEditSubject({ ...editSubject, subject: e.target.value })
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: darkMode ? "#E5E7EB" : "#374151",
                    "& fieldset": {
                      borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                  },
                  "& .MuiInputLabel-root": {
                    color: darkMode ? "#9CA3AF" : "#6B7280",
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#3B82F6" },
                }}
              />

              {/* Description */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={editSubject.description}
                onChange={(e) =>
                  setEditSubject({
                    ...editSubject,
                    description: e.target.value,
                  })
                }
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: darkMode ? "#E5E7EB" : "#374151",
                    "& fieldset": {
                      borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                    },
                    "&:hover fieldset": {
                      borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                  },
                  "& .MuiInputLabel-root": {
                    color: darkMode ? "#9CA3AF" : "#6B7280",
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#3B82F6" },
                }}
              />

              {/* Icon Selection */}
              <div>
                <label
                  className={`block text-sm font-medium ${textPrimary} mb-2`}
                >
                  Subject Icon
                </label>
                <TextField
                  fullWidth
                  value={editSubject.icon}
                  onChange={(e) =>
                    setEditSubject({ ...editSubject, icon: e.target.value })
                  }
                  placeholder="Enter emoji or icon"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: darkMode ? "#E5E7EB" : "#374151",
                      "& fieldset": {
                        borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                    },
                  }}
                />
              </div>

              {/* Popular Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editSubject.isPopular}
                    onChange={(e) =>
                      setEditSubject({
                        ...editSubject,
                        isPopular: e.target.checked,
                      })
                    }
                    color="secondary"
                  />
                }
                label="Mark as Popular Subject"
                sx={{ color: darkMode ? "#E5E7EB" : "#374151" }}
              />

              {/* Exams Management */}
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-3`}>
                  Manage Exams ({editSubject.exams?.length})
                </h3>
                <div className="flex gap-2 mb-4">
                  <TextField
                    fullWidth
                    label="Add New Exam"
                    value={newExam}
                    onChange={(e) => setNewExam(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, "exam")}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: darkMode ? "#E5E7EB" : "#374151",
                        backgroundColor: inputBg,
                        "& fieldset": {
                          borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                        },
                        "&:hover fieldset": {
                          borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                        },
                        "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                      },
                      "& .MuiInputLabel-root": {
                        color: darkMode ? "#9CA3AF" : "#6B7280",
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#3B82F6" },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddExam}
                    disabled={!newExam.trim()}
                    sx={{
                      color: darkMode ? "#E5E7EB" : "#374151",
                      borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                      "&:hover": {
                        borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                        backgroundColor: darkMode
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editSubject.exams?.map((exam, index) => (
                    <Chip
                      key={exam}
                      label={exam}
                      onDelete={() => handleRemoveExam(exam)}
                      color="primary"
                      variant="outlined"
                      sx={{
                        borderColor: darkMode ? "#3B82F6" : "#3B82F6",
                        color: darkMode ? "#93C5FD" : "#1E40AF",
                        backgroundColor: darkMode
                          ? "rgba(59, 130, 246, 0.1)"
                          : "rgba(59, 130, 246, 0.1)",
                        "& .MuiChip-deleteIcon": {
                          color: darkMode ? "#93C5FD" : "#1E40AF",
                          "&:hover": {
                            color: darkMode ? "#60A5FA" : "#2563EB",
                          },
                        },
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className={`flex justify-end space-x-3 p-6 border-t ${headerBorder}`}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  color: darkMode ? "#E5E7EB" : "#374151",
                  borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                  "&:hover": {
                    borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                    backgroundColor: darkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{
                  background: "linear-gradient(135deg, #10B981, #059669)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #059669, #047857)",
                  },
                }}
              >
                Save Changes
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {loading && <Loading />}
    </AnimatePresence>
  );
};

export default UpdateSubject;
